import { useEffect, useMemo, useState } from 'react';
import {
  IconSearch, IconAlertCircle, IconBoxSeam, IconAdjustmentsHorizontal,
  IconPackages, IconClipboardList, IconTrash, IconCheck, IconHistory,
  IconChevronDown, IconChevronRight,
  IconCoffee, IconBeer, IconBowl, IconBread, IconCake, IconCookie,
  IconLeaf, IconSquareDot, IconSparkles, IconAlertTriangle,
  IconCircleCheck, IconCircleX, IconCoin, IconStack2, IconBoxOff,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';

/* -------------------------------------------------------------------------- */
/* Types + helpers                                                            */
/* -------------------------------------------------------------------------- */

interface StockRow {
  productId: string;
  name: string;
  categoryId: string;
  categoryName: string;
  onHand: number;
  reorderPoint: number;
  reorderQty: number;
  lastCostCents: number;
  flagged: boolean;
}

interface ApiCategory {
  id: string;
  name: string;
  tone: string;
  iconKey: string;
}

interface Movement {
  id: string;
  kind: string;
  deltaQty: number;
  unitCostCents: number | null;
  reason: string | null;
  at: string;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}
function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' });
}

const TONE_CLASSES = {
  primary:   { solid: 'bg-primary text-[#FFFFFF]',   soft: 'bg-primary-soft text-primary',     text: 'text-primary'   },
  secondary: { solid: 'bg-secondary text-[#FFFFFF]', soft: 'bg-secondary-soft text-secondary', text: 'text-secondary' },
  orange:    { solid: 'bg-orange text-[#FFFFFF]',    soft: 'bg-orange-soft text-orange',       text: 'text-orange'    },
  info:      { solid: 'bg-info text-[#FFFFFF]',      soft: 'bg-info-soft text-info',           text: 'text-info'      },
  success:   { solid: 'bg-success text-[#FFFFFF]',   soft: 'bg-success-soft text-success',     text: 'text-success'   },
  warning:   { solid: 'bg-warning text-[#FFFFFF]',   soft: 'bg-warning-soft text-warning',     text: 'text-warning'   },
  pink:      { solid: 'bg-pink text-[#FFFFFF]',      soft: 'bg-pink-soft text-pink',           text: 'text-pink'      },
  rose:      { solid: 'bg-rose text-[#FFFFFF]',      soft: 'bg-rose-soft text-rose',           text: 'text-rose'      },
  purple:    { solid: 'bg-purple text-[#FFFFFF]',    soft: 'bg-purple-soft text-purple',       text: 'text-purple'    },
  indigo:    { solid: 'bg-indigo text-[#FFFFFF]',    soft: 'bg-indigo-soft text-indigo',       text: 'text-indigo'    },
  teal:      { solid: 'bg-teal text-[#FFFFFF]',      soft: 'bg-teal-soft text-teal',           text: 'text-teal'      },
  lime:      { solid: 'bg-lime text-[#FFFFFF]',      soft: 'bg-lime-soft text-lime',           text: 'text-lime'      },
  danger:    { solid: 'bg-danger text-[#FFFFFF]',    soft: 'bg-danger-soft text-danger',       text: 'text-danger'    },
} as const;
type ToneKey = keyof typeof TONE_CLASSES;
function toneOf(t: string): ToneKey { return (t in TONE_CLASSES ? t : 'primary') as ToneKey; }

const ICON_BY_KEY: Record<string, typeof IconSparkles> = {
  coffee: IconCoffee, beer: IconBeer, bowl: IconBowl, bread: IconBread,
  cake: IconCake, cookie: IconCookie, leaf: IconLeaf, sparkles: IconSparkles,
  circle: IconSquareDot,
};

const KIND_TONE: Record<string, string> = {
  RECEIVE:      'bg-success-soft text-success',
  ADJUST:       'bg-primary-soft text-primary',
  STOCK_TAKE:   'bg-warning-soft text-warning',
  WASTAGE:      'bg-danger-soft text-danger',
  SALE:         'bg-light text-gray-700',
  SALE_REVERSE: 'bg-light text-gray-700',
  TRANSFER_IN:  'bg-info-soft text-info',
  TRANSFER_OUT: 'bg-info-soft text-info',
};

type ActionKind = 'adjust' | 'waste' | 'receive' | 'stock-take' | 'view';
type StatusFilter = 'all' | 'in_stock' | 'low' | 'out';

function stockStatus(row: StockRow): { key: 'OUT' | 'LOW' | 'OK'; tone: ToneKey; label: string } {
  if (row.onHand <= 0) return { key: 'OUT', tone: 'danger',  label: 'Out of stock' };
  if (row.flagged)     return { key: 'LOW', tone: 'warning', label: 'Low stock'    };
  return                 { key: 'OK',  tone: 'success', label: 'In stock'     };
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

export default function AdminStockPage() {
  const [rows, setRows] = useState<StockRow[]>([]);
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<{ kind: ActionKind; product: StockRow } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        fetch('/api/stock', { credentials: 'include' }),
        fetch('/api/categories', { credentials: 'include' }),
      ]);
      if (!s.ok || !c.ok) throw new Error(`HTTP ${s.status}/${c.status}`);
      const sd = await s.json();
      const cd = await c.json();
      setRows(sd.products ?? []);
      setCategories(cd.categories ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const categoryById = useMemo(() => {
    const m = new Map<string, ApiCategory>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const counts = useMemo(() => {
    let out = 0, low = 0, healthy = 0;
    let onHandTotal = 0;
    let valueCents = 0;
    for (const r of rows) {
      if (r.onHand <= 0)      out++;
      else if (r.flagged)     low++;
      else                    healthy++;
      onHandTotal += r.onHand;
      valueCents  += r.onHand * r.lastCostCents;
    }
    return { total: rows.length, out, low, healthy, onHandTotal, valueCents };
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'out'      && r.onHand > 0)                         return false;
      if (filter === 'low'      && !(r.onHand > 0 && r.flagged))         return false;
      if (filter === 'in_stock' && !(r.onHand > 0 && !r.flagged))        return false;
      if (categoryFilter !== 'all' && r.categoryId !== categoryFilter)   return false;
      if (q && !r.name.toLowerCase().includes(q))                        return false;
      return true;
    });
  }, [rows, search, filter, categoryFilter]);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <AdminLayout
      title="Stock"
      subtitle={`${counts.total} products tracked${counts.out > 0 ? ` · ${counts.out} out of stock` : ''}`}
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {/* KPI row */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KpiTile tone="purple"  label="Products tracked" value={counts.total}              Icon={IconBoxSeam} />
          <KpiTile tone="danger"  label="Out of stock"     value={counts.out}                Icon={IconBoxOff} />
          <KpiTile tone="warning" label="Low stock"        value={counts.low}                Icon={IconAlertTriangle} />
          <KpiTile tone="success" label="Healthy"          value={counts.healthy}            Icon={IconCircleCheck} />
          <KpiTile tone="info"    label="Units on hand"    value={counts.onHandTotal}        Icon={IconStack2} />
          <KpiTile tone="rose"    label="Stock value"      value={fmtR(counts.valueCents)}   Icon={IconCoin} />
        </div>
      )}

      {/* Filter bar */}
      <section className="rounded-lg bg-surface border border-border p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <IconSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products by name"
              className="w-full h-11 pl-10 pr-3 rounded-md bg-light border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="h-11 px-3 rounded-md bg-light border-2 border-border focus:border-primary outline-none text-13 font-semibold text-heading"
          >
            <option value="all">All categories</option>
            {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>

          {/* Status chips */}
          <div className="inline-flex items-stretch rounded-md border-2 border-border overflow-hidden">
            <FilterChip active={filter === 'all'}      onClick={() => setFilter('all')}      label={`All`}         count={counts.total} />
            <FilterChip active={filter === 'in_stock'} onClick={() => setFilter('in_stock')} label={`In stock`}    count={counts.healthy} tone="success" />
            <FilterChip active={filter === 'low'}      onClick={() => setFilter('low')}      label={`Low`}         count={counts.low}     tone="warning" />
            <FilterChip active={filter === 'out'}      onClick={() => setFilter('out')}      label={`Out`}         count={counts.out}     tone="danger" />
          </div>
        </div>
      </section>

      {/* List */}
      <section className="rounded-lg bg-surface border border-border p-2">
        {loading ? (
          <p className="text-13 italic text-gray-700 p-5">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
              <IconBoxSeam size={22} stroke={2} />
            </div>
            <p className="text-14 font-bold text-heading">
              {filter === 'all' ? 'No products to track yet.' : 'Nothing in this filter.'}
            </p>
            <p className="text-13 font-medium text-gray-700 mt-1">
              {filter === 'all' ? 'Add products via /admin/products first.' : 'Switch to a different status above.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {visible.map((p) => {
              const cat = categoryById.get(p.categoryId);
              const tone = toneOf(cat?.tone ?? 'primary');
              const tc = TONE_CLASSES[tone];
              const Icon = ICON_BY_KEY[cat?.iconKey ?? 'circle'] ?? IconSparkles;
              const status = stockStatus(p);
              const statusTc = TONE_CLASSES[status.tone];
              const isOpen = expanded.has(p.productId);
              const value = p.onHand * p.lastCostCents;

              return (
                <li key={p.productId}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleExpand(p.productId)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(p.productId); } }}
                    className="py-3 px-3 flex items-center gap-3 cursor-pointer rounded-md hover:bg-light/60 transition-colors"
                  >
                    {/* Chevron */}
                    <div className="w-5 inline-flex items-center justify-center text-gray-700 flex-shrink-0">
                      {isOpen ? <IconChevronDown size={16} stroke={2.5} /> : <IconChevronRight size={16} stroke={2.5} />}
                    </div>

                    {/* Category icon chip */}
                    <div className={`w-11 h-11 rounded-lg ${tc.solid} inline-flex items-center justify-center flex-shrink-0`}>
                      <Icon size={20} stroke={2} className="text-[#FFFFFF]" />
                    </div>

                    {/* Name + category tag + status */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-14 font-bold text-heading">{p.name}</p>
                        {cat && (
                          <span className={`inline-flex items-center px-2 h-5 rounded-md ${tc.soft} text-11 font-bold uppercase tracking-wider`}>
                            {cat.name}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 h-5 rounded-md ${statusTc.soft} text-11 font-bold uppercase tracking-wider`}>
                          {status.key === 'OUT' ? <IconCircleX size={12} stroke={2.5} className="mr-1" /> :
                           status.key === 'LOW' ? <IconAlertTriangle size={12} stroke={2.5} className="mr-1" /> :
                                                  <IconCircleCheck size={12} stroke={2.5} className="mr-1" />}
                          {status.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-12 text-gray-700 font-medium">
                        <span>Reorder pt <strong className="text-heading tabular-nums">{p.reorderPoint}</strong></span>
                        {p.lastCostCents > 0 && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>Cost <strong className="text-heading tabular-nums">{fmtR(p.lastCostCents)}</strong></span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* On hand stat */}
                    <div className="hidden md:block flex-shrink-0 text-right">
                      <p className={`text-22 font-bold tabular-nums leading-none ${p.onHand <= 0 ? 'text-danger' : p.flagged ? 'text-warning' : 'text-heading'}`}>
                        {p.onHand}
                      </p>
                      <p className="text-10 font-bold uppercase tracking-widest text-gray-700 mt-1">on hand</p>
                    </div>

                    {/* Actions */}
                    <div onClick={(e) => e.stopPropagation()} className="inline-flex gap-1 flex-shrink-0">
                      <ActionBtn label="Receive"    Icon={IconPackages}                 tone="success" onClick={() => setAction({ kind: 'receive', product: p })} />
                      <ActionBtn label="Stock-take" Icon={IconClipboardList}            tone="warning" onClick={() => setAction({ kind: 'stock-take', product: p })} />
                      <ActionBtn label="Adjust"     Icon={IconAdjustmentsHorizontal}    tone="primary" onClick={() => setAction({ kind: 'adjust', product: p })} />
                      <ActionBtn label="Waste"      Icon={IconTrash}                    tone="danger"  onClick={() => setAction({ kind: 'waste', product: p })} />
                      <ActionBtn label="History"    Icon={IconHistory}                  tone="default" onClick={() => setAction({ kind: 'view', product: p })} />
                    </div>
                  </div>

                  {isOpen && (
                    <div className="ml-9 mb-3 mr-3 rounded-md bg-light border border-border-soft p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Mini label="On hand"  value={String(p.onHand)} tone={p.onHand <= 0 ? 'danger' : p.flagged ? 'warning' : 'success'} />
                      <Mini label="Reorder pt"  value={String(p.reorderPoint)} />
                      <Mini label="Reorder qty" value={String(p.reorderQty)} />
                      <Mini label="Stock value" value={fmtR(value)} tone="info" />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {action && (
        <ActionModal
          kind={action.kind}
          product={action.product}
          onClose={() => setAction(null)}
          onSaved={() => { void load(); setAction(null); }}
        />
      )}
    </AdminLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Bits                                                                        */
/* -------------------------------------------------------------------------- */

function KpiTile({ tone, label, value, Icon }: { tone: ToneKey; label: string; value: number | string; Icon: typeof IconSparkles }) {
  const tc = TONE_CLASSES[tone];
  return (
    <div className={`rounded-lg ${tc.soft} p-4 flex items-center justify-between gap-3 hover:shadow-md transition-shadow`}>
      <div className="flex-1 min-w-0">
        <p className="text-24 font-bold text-heading tabular-nums leading-none truncate">{value}</p>
        <p className="text-13 font-semibold text-gray-700 mt-1 truncate">{label}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg ${tc.solid} inline-flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} stroke={2.25} className="text-[#FFFFFF]" />
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, tone }: { active: boolean; onClick: () => void; label: string; count: number; tone?: ToneKey }) {
  const tc = tone ? TONE_CLASSES[tone] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'h-11 px-4 inline-flex items-center gap-2 text-13 font-bold transition-colors border-r-2 last:border-r-0 border-border',
        active
          ? tc ? `${tc.solid}` : 'bg-primary text-[#FFFFFF]'
          : 'bg-light text-heading hover:bg-gray-100',
      ].join(' ')}
    >
      <span>{label}</span>
      <span className={[
        'px-1.5 py-0.5 rounded text-11 font-bold tabular-nums',
        active ? 'bg-[rgba(255,255,255,0.25)]' : 'bg-surface border border-border',
      ].join(' ')}>{count}</span>
    </button>
  );
}

function Mini({ label, value, tone = 'primary' }: { label: string; value: string; tone?: ToneKey }) {
  const tc = TONE_CLASSES[tone];
  return (
    <div>
      <p className="text-11 font-bold uppercase tracking-wider text-gray-700">{label}</p>
      <p className={`text-22 font-bold tabular-nums mt-1 ${tc.text}`}>{value}</p>
    </div>
  );
}

function ActionBtn({ label, Icon, tone, onClick }: { label: string; Icon: typeof IconBoxSeam; tone: 'default' | 'primary' | 'success' | 'warning' | 'danger'; onClick: () => void }) {
  const cls = {
    default: 'text-gray-700 hover:bg-light border-border',
    primary: 'text-primary hover:bg-primary-soft border-primary/30',
    success: 'text-success hover:bg-success-soft border-success/30',
    warning: 'text-warning hover:bg-warning-soft border-warning/30',
    danger:  'text-danger  hover:bg-danger-soft  border-danger/30',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-10 h-10 inline-flex items-center justify-center rounded-md border-2 transition-colors ${cls}`}
      title={label}
    >
      <Icon size={16} stroke={2.25} />
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* ActionModal — unchanged from prior version                                  */
/* -------------------------------------------------------------------------- */

function ActionModal({
  kind, product, onClose, onSaved,
}: {
  kind: ActionKind;
  product: StockRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [unitCostRand, setUnitCostRand] = useState((product.lastCostCents / 100).toFixed(2));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<Movement[]>([]);
  const [historyLoading, setHistoryLoading] = useState(kind === 'view');

  useEffect(() => {
    setQty(''); setReason(''); setReference(''); setNotes('');
    setUnitCostRand((product.lastCostCents / 100).toFixed(2));
    setErr(null);
    if (kind === 'view') {
      setHistoryLoading(true);
      (async () => {
        try {
          const r = await fetch(`/api/stock/${product.productId}/movements`, { credentials: 'include' });
          if (r.ok) {
            const data = await r.json();
            setHistory(data.movements ?? []);
          }
        } finally {
          setHistoryLoading(false);
        }
      })();
    }
  }, [kind, product]);

  async function save() {
    setSaving(true); setErr(null);
    try {
      let url = '';
      let body: Record<string, unknown> = {};
      const qtyInt = Number(qty);
      if (kind === 'adjust') {
        if (!isFinite(qtyInt) || qtyInt === 0) throw new Error('Enter a non-zero delta');
        if (!reason.trim()) throw new Error('Reason is required');
        url = `/api/stock/${product.productId}/adjust`;
        body = { deltaQty: qtyInt, reason: reason.trim() };
      } else if (kind === 'waste') {
        if (!isFinite(qtyInt) || qtyInt <= 0) throw new Error('Qty must be > 0');
        if (!reason.trim()) throw new Error('Reason is required');
        url = `/api/stock/${product.productId}/waste`;
        body = { qty: qtyInt, reason: reason.trim() };
      } else if (kind === 'receive') {
        if (!isFinite(qtyInt) || qtyInt <= 0) throw new Error('Qty must be > 0');
        const unit = Math.round(Number(unitCostRand.replace(',', '.')) * 100);
        url = `/api/stock/${product.productId}/receive`;
        body = {
          qty: qtyInt,
          unitCostCents: unit > 0 ? unit : undefined,
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
        };
      } else if (kind === 'stock-take') {
        const counted = Number(qty);
        if (!isFinite(counted) || counted < 0) throw new Error('Counted qty must be ≥ 0');
        url = `/api/stock/${product.productId}/stock-take`;
        body = { countedQty: counted, reason: reason.trim() || undefined };
      }
      const r = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? `HTTP ${r.status}`);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (kind === 'view') {
    return (
      <Modal open onClose={onClose} size="lg" title={`Stock movements · ${product.name}`}>
        <p className="text-13 text-gray-700 mb-3">
          Current on hand: <strong className="text-heading tabular-nums">{product.onHand}</strong>
        </p>
        {historyLoading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-13 italic text-gray-700">No movements yet.</p>
        ) : (
          <ul className="space-y-2 max-h-[480px] overflow-y-auto">
            {history.map((m) => (
              <li key={m.id} className="flex items-start gap-3 p-3 rounded-md bg-light border border-border-soft">
                <span className={`inline-flex items-center px-2 h-6 rounded-md text-11 font-bold uppercase tracking-wider ${KIND_TONE[m.kind] ?? 'bg-light text-gray-700'}`}>
                  {m.kind.replace(/_/g, ' ')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-14 font-bold text-heading">
                    {m.deltaQty > 0 ? `+${m.deltaQty}` : m.deltaQty}
                    {m.unitCostCents !== null && <span className="ml-2 text-12 font-normal text-gray-700">@ {fmtR(m.unitCostCents)} ea</span>}
                  </p>
                  {m.reason && <p className="text-12 italic text-gray-700">"{m.reason}"</p>}
                </div>
                <p className="text-12 font-medium text-gray-700 tabular-nums whitespace-nowrap">{fmtDateTime(m.at)}</p>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    );
  }

  const title = {
    adjust:       `Adjust · ${product.name}`,
    waste:        `Wastage · ${product.name}`,
    receive:      `Receive stock · ${product.name}`,
    'stock-take': `Stock-take · ${product.name}`,
    view:         '',
  }[kind];

  const ctaTone = {
    adjust:       'bg-primary hover:bg-primary-hover',
    waste:        'bg-danger hover:bg-danger-hover',
    receive:      'bg-success hover:bg-success-hover',
    'stock-take': 'bg-warning hover:bg-warning-hover',
    view:         '',
  }[kind];

  return (
    <Modal open onClose={onClose} size="sm" title={title}>
      <p className="text-12 text-gray-700 mb-4">
        Current on hand: <strong className="text-heading tabular-nums">{product.onHand}</strong>
      </p>

      {kind === 'adjust' && (
        <>
          <label className="block mb-3">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
              Delta (signed: +5 to add, -3 to remove)
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={qty}
              onChange={(e) => setQty(e.target.value.replace(/[^0-9-]/g, ''))}
              className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Reason <span className="text-danger">*</span></span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. owner manual count correction"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </label>
        </>
      )}

      {kind === 'waste' && (
        <>
          <label className="block mb-3">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Qty discarded</span>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={qty}
              onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Reason <span className="text-danger">*</span></span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. expired, spilled, broken"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </label>
        </>
      )}

      {kind === 'receive' && (
        <>
          <label className="block mb-3">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Qty received</span>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={qty}
              onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading"
            />
          </label>
          <label className="block mb-3">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Unit cost (R) — optional</span>
            <input
              type="text"
              inputMode="decimal"
              value={unitCostRand}
              onChange={(e) => setUnitCostRand(e.target.value.replace(/[^0-9.,]/g, ''))}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-bold tabular-nums text-heading"
            />
          </label>
          <label className="block mb-3">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Supplier ref — optional</span>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. invoice number"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Notes — optional</span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </label>
        </>
      )}

      {kind === 'stock-take' && (
        <>
          <label className="block mb-3">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Counted qty</span>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={qty}
              onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder={String(product.onHand)}
              className="w-full h-12 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading"
            />
          </label>
          {qty && Number(qty) !== product.onHand && (
            <p className="text-13 font-semibold text-warning mb-3">
              Delta: {Number(qty) - product.onHand > 0 ? '+' : ''}{Number(qty) - product.onHand} (was {product.onHand})
            </p>
          )}
          <label className="block mb-4">
            <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Note — optional</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. weekly count Friday close"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </label>
        </>
      )}

      {err && <p className="text-13 font-semibold text-danger mb-3">{err}</p>}

      <div className="flex items-stretch gap-2">
        <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
        <button
          onClick={save}
          disabled={saving}
          className={`flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md ${ctaTone} text-[#FFFFFF] text-14 font-bold disabled:bg-gray-300`}
        >
          <IconCheck size={16} stroke={2.5} />
          {saving ? 'Saving…' : 'Record'}
        </button>
      </div>
    </Modal>
  );
}
